import React, { useState, useRef, useEffect } from 'react';
import { MenuItem, Menu } from '@mui/material';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';

interface NestedMenuItemProps {
    label: string;
    children?: React.ReactNode;
    onClick?: () => void;
}

const NestedMenuItem: React.FC<NestedMenuItemProps> = ({ label, children, onClick }) => {
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const menuItemRef = useRef<HTMLLIElement>(null);
    const timeoutRef = useRef<NodeJS.Timeout | null>(null);
    const isSubmenuOpen = Boolean(anchorEl);

    // Clear timeout khi component unmount
    useEffect(() => {
        return () => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
        };
    }, []);

    const handleMouseEnter = (event: React.MouseEvent<HTMLElement>) => {
        // Clear timeout nếu đang có
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
            timeoutRef.current = null;
        }

        if (children) {
            setAnchorEl(event.currentTarget);
        }
    };

    const handleMouseLeave = () => {
        // Delay 200ms trước khi đóng submenu
        timeoutRef.current = setTimeout(() => {
            setAnchorEl(null);
        }, 200);
    };

    const handleSubmenuMouseEnter = () => {
        // Khi hover vào submenu, hủy timeout đóng
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
            timeoutRef.current = null;
        }
    };

    const handleSubmenuMouseLeave = () => {
        // Delay 200ms trước khi đóng submenu
        timeoutRef.current = setTimeout(() => {
            setAnchorEl(null);
        }, 200);
    };

    const handleClick = () => {
        if (onClick && !children) {
            onClick();
        }
    };

    return (
        <>
            <MenuItem
                ref={menuItemRef}
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
                onClick={handleClick}
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    '&:hover': {
                        backgroundColor: '#f5f5f5'
                    }
                }}
            >
                <span>{label}</span>
                {children && <ChevronRightIcon sx={{ ml: 2, fontSize: '18px' }} />}
            </MenuItem>

            {children && (
                <Menu
                    anchorEl={anchorEl}
                    open={isSubmenuOpen}
                    onClose={() => setAnchorEl(null)}
                    anchorOrigin={{
                        vertical: 'top',
                        horizontal: 'right',
                    }}
                    transformOrigin={{
                        vertical: 'top',
                        horizontal: 'left',
                    }}
                    MenuListProps={{
                        onMouseEnter: handleSubmenuMouseEnter,
                        onMouseLeave: handleSubmenuMouseLeave,
                        sx: { py: 0 }
                    }}
                    sx={{
                        pointerEvents: 'none',
                        '& .MuiPaper-root': {
                            pointerEvents: 'auto',
                            ml: 0.5
                        }
                    }}
                >
                    {children}
                </Menu>
            )}
        </>
    );
};

export default NestedMenuItem;
